<template>
    <div class="compute-metrics-section">
        <div class="section-divider">
            <span class="section-title">Compute & Block Space</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Block Space Distribution</div>
                    <div class="panel-subtitle">Compute units consumed by program</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="blockSpaceChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Compute Efficiency</div>
                    <div class="panel-subtitle">CU used vs CU requested (higher = more efficient)</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="efficiencyChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Compute Usage Over Time</div>
                    <div class="panel-subtitle">Top programs + Others</div>
                </div>
                <div class="panel-body chart-extra-tall">
                    <canvas ref="computeTimeChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';
import { formatCompact, formatPubkey, programLabel, getEfficiencyColor } from '../../composables/useFormatters';
import { colors, getBarChartOptions, getStackedBarChartOptions, systemPrograms } from '../../composables/useChartConfig';
import { GRANULARITY_MODES, transformValue, getUnitSuffix } from '../../composables/useGranularityToggle';

// Register treemap components
Chart.register(TreemapController, TreemapElement);

export default {
    name: 'ProgramComputeMetrics',

    props: {
        programs: {
            type: Array,
            required: true
        },
        interval: {
            type: String,
            default: '1h'
        },
        range: {
            type: String,
            default: '24h'
        },
        showSystemPrograms: {
            type: Boolean,
            default: false
        },
        granularityMode: {
            type: String,
            default: GRANULARITY_MODES.TOTAL
        }
    },

    data() {
        return {
            charts: {},
            colors: colors,
            computeData: null,
            loading: false
        };
    },

    watch: {
        programs: {
            handler() {
                if (Object.keys(this.charts).length > 0) {
                    this.updateCharts();
                }
            },
            deep: true
        },
        interval() {
            this.fetchComputeData();
        },
        range() {
            this.fetchComputeData();
        },
        showSystemPrograms() {
            this.updateCharts();
        },
        granularityMode() {
            if (Object.keys(this.charts).length > 0) {
                this.updateCharts();
            }
        }
    },

    mounted() {
        this.initCharts();
        this.fetchComputeData();
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
    },

    methods: {
        formatCompact,
        formatPubkey,
        programLabel,

        transformForGranularity(value) {
            return transformValue(value, this.granularityMode, this.interval);
        },

        getGranularityLabel(baseLabel) {
            const suffix = getUnitSuffix(this.granularityMode, this.interval, '');
            return suffix ? `${baseLabel} ${suffix}` : baseLabel;
        },

        initCharts() {
            // Block Space Chart (Treemap)
            this.charts.blockSpace = markRaw(new Chart(this.$refs.blockSpaceChart, {
                type: 'treemap',
                data: {
                    datasets: [{
                        tree: [],
                        key: 'value',
                        groups: ['label'],
                        spacing: 1,
                        borderWidth: 1,
                        borderColor: 'rgba(255,255,255,0.2)',
                        backgroundColor: (ctx) => {
                            if (!ctx.raw) return this.colors.series[0];
                            const item = ctx.raw;
                            // Color based on CU efficiency (higher = greener)
                            const efficiency = item._data?.efficiency || 0;
                            return getEfficiencyColor(efficiency);
                        },
                        labels: {
                            display: true,
                            formatter: (ctx) => ctx.raw?._data?.label || ''
                        }
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                title: (items) => items[0]?.raw?._data?.label || '',
                                label: (item) => {
                                    const data = item.raw?._data;
                                    if (!data) return '';
                                    return [
                                        `Compute: ${formatCompact(data.value)} CU`,
                                        `Efficiency: ${(data.efficiency * 100).toFixed(1)}%`
                                    ];
                                }
                            }
                        }
                    }
                }
            }));

            // Efficiency Chart
            this.charts.efficiency = markRaw(new Chart(this.$refs.efficiencyChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: {
                    ...getBarChartOptions({ horizontal: true }),
                    scales: {
                        x: {
                            max: 100,
                            title: { display: true, text: 'CU Efficiency %', color: '#9ca3af' },
                            ticks: { color: '#9ca3af' },
                            grid: { color: 'rgba(255,255,255,0.05)' }
                        },
                        y: {
                            ticks: { color: '#9ca3af' },
                            grid: { color: 'rgba(255,255,255,0.05)' }
                        }
                    }
                }
            }));

            // Compute Time Chart (Stacked)
            this.charts.computeTime = markRaw(new Chart(this.$refs.computeTimeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({ formatCallback: v => formatCompact(v) })
            }));

            this.updateCharts();
        },

        async fetchComputeData() {
            this.loading = true;
            try {
                const params = new URLSearchParams({
                    interval: this.interval,
                    range: this.range,
                    limit: 50 // Get more programs for better Others calculation
                });
                const response = await fetch(`/analytics/api/compute-share?${params}`);
                const data = await response.json();
                this.computeData = data;
                this.updateCharts();
            } catch (error) {
                console.error('Failed to fetch compute data:', error);
                // Fall back to using programs prop
                this.computeData = null;
                this.updateCharts();
            } finally {
                this.loading = false;
            }
        },

        // Filter programs based on system programs toggle
        filterPrograms(programs) {
            if (this.showSystemPrograms) {
                return programs;
            }
            return programs.filter(p => !systemPrograms.includes(p.program));
        },

        updateCharts() {
            // Use dedicated compute data if available, otherwise fall back to props
            const useComputeData = this.computeData?.programs?.length > 0;

            // For efficiency chart, always use programs prop (has cuEfficiency)
            const withEff = this.filterPrograms(this.programs)
                .filter(p => p.metrics.cuEfficiency > 0)
                .slice(0, 10);

            // For treemap and time series, prefer dedicated compute data
            let computePrograms;
            if (useComputeData) {
                computePrograms = this.filterPrograms(this.computeData.programs);
            } else {
                computePrograms = this.filterPrograms(this.programs)
                    .map(p => ({
                        program: p.program,
                        name: p.name,
                        totalCompute: p.metrics.totalCompute,
                        computeShare: p.metrics.computeShare || 0,
                        timeSeries: p.timeSeries?.map(t => ({ compute: t.compute, label: t.label })) || []
                    }));
            }

            const byCompute = [...computePrograms]
                .sort((a, b) => b.totalCompute - a.totalCompute)
                .slice(0, 10);

            const topN = [...computePrograms]
                .sort((a, b) => b.totalCompute - a.totalCompute)
                .slice(0, 25);

            const timeLabels = topN[0]?.timeSeries?.map(t => t.label) || [];

            // Block Space (Treemap) - uses compute data
            const treemapData = byCompute.map(p => {
                // Try to get efficiency from programs prop if available
                const programFromProps = this.programs.find(pp => pp.program === p.program);
                const efficiency = programFromProps?.metrics?.cuEfficiency || 0;
                return {
                    label: programLabel(p),
                    value: p.totalCompute,
                    efficiency: efficiency
                };
            });
            this.charts.blockSpace.data.datasets[0].tree = treemapData;
            this.charts.blockSpace.update();

            // Efficiency (uses programs prop for cuEfficiency data)
            this.charts.efficiency.data = {
                labels: withEff.map(p => programLabel(p)),
                datasets: [{
                    data: withEff.map(p => p.metrics.cuEfficiency * 100),
                    backgroundColor: withEff.map(p => getEfficiencyColor(p.metrics.cuEfficiency)),
                    borderWidth: 0
                }]
            };
            this.charts.efficiency.update();

            // Compute Time - 25 programs + Others (uses dedicated compute data)
            const totalCompute = useComputeData ? this.computeData.totalCompute :
                computePrograms.reduce((sum, p) => sum + p.totalCompute, 0);
            const transform = this.transformForGranularity.bind(this);

            const othersData = timeLabels.map((_, timeIdx) => {
                const topNTotal = topN.reduce((sum, p) => {
                    return sum + (p.timeSeries?.[timeIdx]?.compute || 0);
                }, 0);
                const allTotal = computePrograms.reduce((sum, p) => {
                    return sum + (p.timeSeries?.[timeIdx]?.compute || 0);
                }, 0);
                return transform(Math.max(0, allTotal - topNTotal));
            });

            const datasets = topN.map((p, i) => ({
                label: programLabel(p),
                data: p.timeSeries?.map(t => transform(t.compute)) || [],
                backgroundColor: this.colors.series[i % this.colors.series.length],
                borderWidth: 0
            }));

            // Add "Others" category
            if (othersData.some(v => v > 0)) {
                datasets.push({
                    label: 'Others',
                    data: othersData,
                    backgroundColor: '#4b5563',
                    borderWidth: 0
                });
            }

            this.charts.computeTime.data = {
                labels: timeLabels,
                datasets: datasets
            };
            this.charts.computeTime.update();
        }
    }
};
</script>

<style scoped>
.chart-tall {
    min-height: 280px;
}
</style>
